import { of, concat, race } from 'rxjs'
import { map, concatMap, switchMap, flatMap } from 'rxjs/operators'
import { mapObjIndexed } from 'ramda'

import { createStoreNavigateTo$ } from './navigation'
import {
  createClearLoading$,
  createLoading$,
} from '@apiEnabler/api-enabler-common/lib/loading'
import {
  createModal$,
  clearModal$,
} from '@apiEnabler/api-enabler-common/lib/dialogs'

import {
  updateAccessStateAfterLoad,
  updateAccessHistoryState,
  updateAccessesGeneratedState$,
} from '@apiEnabler/ob-common/lib/access-consents'

import { initialData$ } from './customer'
import {
  displayAccesses$,
  displayAccessesForTpp$,
  displayAccessDetails$,
  ceaseCancelAccess$,
  confirmCancelAccess$,
  cancelAccess$,
} from './intents'
import { createCancelConsent$, createGetConsentAudits$ } from './services'
import { isStaffMember } from './staff'

const addKeyAsNameProperty = mapObjIndexed((value, key) => ({
  ...value,
  name: key,
}))

let order = 0

export const ACCESS_SECTIONS = addKeyAsNameProperty({
  active: {
    label: 'Active',
    description: `You shared information with a service provider to view the agreed account and transaction information for the specified account(s) for an agreed period. \n\n You'll need to reconfirm your consent with your service provider every 90 days. \n\n You can stop sharing this information at anytime.`,
  },
  deleted: {
    label: 'Deleted',
    description: `You shared information to a service provider to view the agreed account and transaction information for the specified account(s) for an agreed period. \n\n You stopped sharing (and disconnected) this access through your service provider.`,
    order: order++,
  },
  expired: {
    label: 'Expired',
    description: `You shared information to a service provider to view the agreed account and transaction information for the specified account(s) for an agreed period. \n\n If the connection isn't reconfirmed, it will change from active to expired.`,
  },
  revoked: {
    label: 'Revoked',
    description:
      'The service provide has been revoked by the Open Banking Implementation Entity (OBIE). This cannot be undone.',
    order: order++,
  },
  suspended: {
    label: 'Suspended',
    description:
      'To protect the Bank and its customers, the Bank (account provider) may temporarily suspend a service provider. Once the reason(s) for this suspension has been resolved, the agreement will be automatically reinstated by the account provider.',
  },
  withdrawn: {
    label: 'Cancelled',
    description:
      'You confirmed access to a service provider to view the agreed account and transaction information for the specified account(s) for an agreed period. \n\n You stopped sharing this information.',
    order: order++,
  },
  ...(isStaffMember
    ? {
        blocked: {
          label: 'Blocked',
          description: `This status is not displayed to the customer. \n\n A customer has started the process to share information to a service provider to view the agreed account and transaction information for the specified account(s) for an agreed period. \n\n This agreement request has been blocked. This state occurs where there are creditable reasons and/or concerns that mean allowing agreement could potentially be harmful to the Bank and customer and is therefore denied.`,
          order: order++,
          info: 'Only visible to staff',
        },
        declined: {
          label: 'Declined',
          description: `This status is not displayed to the customer. \n\n A customer has started the process to share information to a service provider to view the agreed account and transaction information for the specified account(s) for an agreed period but has cancelled this authorisation process.`,
          order: order++,
          info: 'Only visible to staff',
        },
        created: {
          label: 'Created',
          description:
            'The customer has provided permission to a service provider to have access to specified account(s) for an agreed period. It is in the process of being authorised. Once authorised it will show as active. This state is only viewable to staff.',
          order: order++,
          info: 'Only visible to staff',
        },
      }
    : {}),
})

export function categoriseAccesses(access) {
  switch (access.accessGrouping) {
    case 'ACTIVE':
      return access.status === 'ACCEPTED'
        ? isStaffMember
          ? ACCESS_SECTIONS.created
          : null
        : ACCESS_SECTIONS.active
    case 'DELETED':
      return ACCESS_SECTIONS.deleted
    case 'EXPIRED':
      return ACCESS_SECTIONS.expired
    case 'TPP_REVOKED':
      return ACCESS_SECTIONS.revoked
    case 'TPP_SUSPENDED':
    case 'TPP_CLIENT_SUSPENDED':
      return ACCESS_SECTIONS.suspended
    case 'WITHDRAWN':
      return ACCESS_SECTIONS.withdrawn
    case 'BLOCKED':
      return isStaffMember ? ACCESS_SECTIONS.blocked : null
    case 'DECLINED':
      return isStaffMember ? ACCESS_SECTIONS.declined : null
    default:
      return null // We don't show any consents not grouped by the above grouping
  }
}

export const accessesDisplay$ = displayAccesses$.pipe(
  switchMap(() =>
    concat(
      initialData$,
      updateInitialData$,
      createLoading$('Loading third parties with access agreements'),
      createStoreNavigateTo$('/access-agreements/tpps'),
      updateAccessesGeneratedState$,
      createClearLoading$('Loading third parties with access agreements')
    )
  )
)

export const accessesForTppDisplay$ = displayAccessesForTpp$.pipe(
  switchMap(clientId =>
    concat(
      initialData$,
      updateInitialData$,
      createLoading$('Loading access agreements for service provider'),
      createStoreNavigateTo$(`/access-agreements/tpps/${clientId}/agreements`),
      updateAccessesGeneratedState$,
      createClearLoading$('Loading access agreements for service provider')
    )
  )
)

function createLoadAccessAudits$(accessId) {
  return createGetConsentAudits$(accessId).pipe(
    concatMap(consentAudits =>
      of(updateAccessHistoryState(accessId, consentAudits))
    )
  )
}

function createCancelAccess$(access) {
  return createCancelConsent$(access).pipe(
    map(access =>
      access
        .set('grouping', access.get('status'))
        .set('accessGrouping', access.get('status'))
    ),
    concatMap(access =>
      createGetConsentAudits$(access.get('id')).pipe(
        map(consentAudits => updateAccessStateAfterLoad(access, consentAudits))
      )
    )
  )
}

export const accessDetailsDisplay$ = displayAccessDetails$.pipe(
  switchMap(({ clientId, accessId }) =>
    concat(
      initialData$,
      updateInitialData$,
      createLoading$('Loading access agreement details'),
      createStoreNavigateTo$(
        `/access-agreements/tpps/${clientId}/agreements/${accessId}`
      ),
      updateAccessesGeneratedState$,
      createLoadAccessAudits$(accessId),
      createClearLoading$('Loading access agreement details')
    )
  )
)

export const accessCancel$ = cancelAccess$.pipe(
  flatMap(access =>
    concat(
      createModal$('access-cancel.confirm', access),
      race(
        concat(
          confirmCancelAccess$,
          createModal$('access-cancel.loading'),
          createCancelAccess$(access),
          clearModal$
        ),
        concat(ceaseCancelAccess$, clearModal$)
      )
    )
  )
)

const updateInitialData$ = of(state =>
  state.updateIn(['accesses', 'index'], accesses =>
    accesses.filter(access => categoriseAccesses(access) !== null)
  )
)
